import type { ReactNode } from "react";
import type { ColumnTooltipOption } from "@/types/common";
import TableCellTooltip from "./TableCellTooltip";

// Shared by Table, DraggableQuestionTable and SimpleTable so a plain
// `selector` column gets the same only-when-truncated tooltip in all
// three — see `ColumnTooltipOption` in types/common.ts.

interface AutoTooltipColumn {
  cell?: unknown;
  selector?: unknown;
  tooltip?: ColumnTooltipOption | undefined;
}

// A `cell` column renders its own JSX, so it's left alone; only a plain
// `selector` column that hasn't opted out gets wrapped.
export const shouldAutoTooltip = (column: AutoTooltipColumn) =>
  !column.cell && typeof column.selector === "function" && column.tooltip !== false;

interface RenderAutoTooltipCellOptions {
  tooltip?: ColumnTooltipOption | undefined;
  align?: "left" | "center" | "right" | undefined;
  allowRowEvents?: boolean;
}

export const renderAutoTooltipCell = (
  value: unknown,
  { tooltip, align, allowRowEvents = false }: RenderAutoTooltipCellOptions = {},
): ReactNode => {
  // `selector` is typed to return a primitive; a boolean is stringified
  // the same way SimpleTable already rendered it.
  const cellValue = typeof value === "string" || typeof value === "number" ? value : value == null ? null : String(value);

  return (
    <TableCellTooltip
      value={cellValue}
      {...(typeof tooltip === "object" ? tooltip : {})}
      {...(align && { align })}
      allowRowEvents={allowRowEvents}
    />
  );
};
